import { existsSync, mkdirSync, readFileSync, writeFileSync } from "fs";
import { dirname, join } from "path";
import lockfile from "proper-lockfile";
import { CONFIG_DIR_NAME } from "../config.js";
import { parseJsonFileContent } from "../utils/json.ts";
import { normalizePath, resolvePath } from "../utils/paths.ts";
import { normalizeAzureSettings } from "./azure-provider-compat.js";
import { deepMergeSettings } from "./settings-merge.js";
import type { Settings, SettingsFieldOrigin, SettingsScope, SettingsStorage } from "./settings-types.ts";

export class FileSettingsStorage implements SettingsStorage {
	private globalSettingsPath: string;
	private projectSettingsPath: string;
	private globalReadPaths: string[];
	private projectReadPaths: string[];

	constructor(cwd: string, agentDir: string, options?: { globalReadPaths?: string[]; projectReadPaths?: string[] }) {
		const resolvedCwd = resolvePath(cwd);
		const resolvedAgentDir = resolvePath(agentDir);
		this.globalSettingsPath = join(resolvedAgentDir, "settings.json");
		this.projectSettingsPath = join(resolvedCwd, CONFIG_DIR_NAME, "settings.json");
		this.globalReadPaths = (options?.globalReadPaths ?? [this.globalSettingsPath]).map((path) => normalizePath(path));
		this.projectReadPaths = (options?.projectReadPaths ?? [this.projectSettingsPath]).map((path) =>
			normalizePath(path),
		);
	}

	getFieldOrigin(scope: SettingsScope, field: keyof Settings): SettingsFieldOrigin | undefined {
		const readPaths = scope === "global" ? this.globalReadPaths : this.projectReadPaths;
		for (const [index, readPath] of readPaths.entries()) {
			if (!existsSync(readPath)) continue;
			try {
				const parsed = parseJsonFileContent(readFileSync(readPath, "utf-8")) as Settings;
				if (Object.hasOwn(parsed, field)) {
					return index === 0 ? "primary" : "legacy";
				}
			} catch {
				return undefined;
			}
		}
		return undefined;
	}

	private acquireLockSyncWithRetry(path: string): () => void {
		const maxAttempts = 10;
		const delayMs = 20;
		let lastError: unknown;

		for (let attempt = 1; attempt <= maxAttempts; attempt++) {
			try {
				return lockfile.lockSync(path, { realpath: false });
			} catch (error) {
				const code =
					typeof error === "object" && error !== null && "code" in error
						? String((error as { code?: unknown }).code)
						: undefined;
				if (code !== "ELOCKED" || attempt === maxAttempts) {
					throw error;
				}
				lastError = error;
				const start = Date.now();
				while (Date.now() - start < delayMs) {
					// Sleep synchronously to avoid changing callers to async.
				}
			}
		}

		throw (lastError as Error) ?? new Error("Failed to acquire settings lock");
	}

	private readMergedSettings(readPaths: string[]): string | undefined {
		let merged: Settings = {};
		let found = false;
		for (let i = readPaths.length - 1; i >= 0; i--) {
			const readPath = readPaths[i]!;
			if (!existsSync(readPath)) continue;
			try {
				const parsed = parseJsonFileContent(readFileSync(readPath, "utf-8")) as Settings;
				merged = deepMergeSettings(merged, normalizeAzureSettings(parsed));
				found = true;
			} catch (error) {
				if (error instanceof Error) (error as Error & { path?: string }).path = readPath;
				throw error;
			}
		}
		return found ? JSON.stringify(merged, null, 2) : undefined;
	}

	withLock(scope: SettingsScope, fn: (current: string | undefined) => string | undefined): void {
		const path = scope === "global" ? this.globalSettingsPath : this.projectSettingsPath;
		const readPaths = scope === "global" ? this.globalReadPaths : this.projectReadPaths;
		const dir = dirname(path);

		let release: (() => void) | undefined;
		try {
			// Only create directory and lock if the primary file exists or we need to write.
			const fileExists = existsSync(path);
			if (fileExists) {
				release = this.acquireLockSyncWithRetry(path);
			}
			const current = this.readMergedSettings(readPaths);
			const next = fn(current);

			if (next !== undefined) {
				// Only create directory when we actually need to write.
				if (!existsSync(dir)) {
					mkdirSync(dir, { recursive: true });
				}
				if (!release) {
					if (!existsSync(path)) writeFileSync(path, "{}", "utf-8");
					release = this.acquireLockSyncWithRetry(path);
				}
				writeFileSync(path, next, "utf-8");
			}
		} finally {
			if (release) {
				release();
			}
		}
	}

	withPrimaryWriteLock(scope: SettingsScope, fn: (currentPrimary: string | undefined) => string | undefined): void {
		const path = scope === "global" ? this.globalSettingsPath : this.projectSettingsPath;
		const dir = dirname(path);
		if (!existsSync(dir)) {
			mkdirSync(dir, { recursive: true });
		}

		const release = this.acquireLockSyncWithRetry(path);
		try {
			const currentPrimary = existsSync(path) ? readFileSync(path, "utf-8") : undefined;
			const next = fn(currentPrimary);
			if (next !== undefined) {
				writeFileSync(path, next, "utf-8");
			}
		} finally {
			release();
		}
	}
}

export class InMemorySettingsStorage implements SettingsStorage {
	private global: string | undefined;
	private project: string | undefined;

	withLock(scope: SettingsScope, fn: (current: string | undefined) => string | undefined): void {
		const current = scope === "global" ? this.global : this.project;
		const next = fn(current);
		if (next !== undefined) {
			if (scope === "global") {
				this.global = next;
			} else {
				this.project = next;
			}
		}
	}

	getFieldOrigin(scope: SettingsScope, field: keyof Settings): SettingsFieldOrigin | undefined {
		const raw = scope === "global" ? this.global : this.project;
		const settings = JSON.parse(raw ?? "{}") as Settings;
		return Object.hasOwn(settings, field) ? "primary" : undefined;
	}
}
